import Err from '@leverege/error'
import fetchRetry from 'fetch-retry'

import { encodeQ, getPatchOpsArray, combinePath } from './Utils.js'

const ApiError = Err.extend( 'ApiError', 400, 'api error' )

function ApiErrorFactory( json, response ) {
  const err = ApiError( { ...json } )
  Object.defineProperty( err, 'response', { get() { return response } } )
  return err
}

function authToken( token, type = 'Bearer' ) {
  return { getToken( ) { return type == null ? token : `${type} ${token}` } }
}

/**
 * Creates a Comms class. To use this, use the chaining calls:
 * <pre>
 *  const comms = new Comms( { host } )
 *  await comms.child( 'path' ).child( 'to' ).child( 'resource' ).get( params )
 * </pre>
 *
 * @param {string} host the base host eg name: http://my.server.com:8121
 * @param {boolean} retry if true this will install fetch-retry. (Default is true)
 * @param {object} retryOptions the options handed to fetch-retry
 */
class Comms {
  constructor( opts ) {
    const { host, retry = true, retryOptions, fetch : customFetch, mutateRequest } = opts || {}
    this.options = opts
    this.host = host
    if ( this.host && this.host.endsWith( '/' ) ) {
      this.host = this.host.substring( 0, this.host.length - 1 )
    }

    const useFetch = customFetch || fetch

    if ( retry ) {
      /**
       * @type {import('@adobe/fetch').fetch}
       */
      this.fetch = retry ? fetchRetry( useFetch, retryOptions ) : useFetch
    }

    this.mutateRequest = mutateRequest
  }

  /**
   * Returns the authentication token
   */
  getToken( method, url, extraParams ) {
    const auth = this.options.auth
    if ( typeof auth === 'string' ) {
      return auth
    }
    if ( auth && typeof auth.getToken === 'function' ) {
      return auth.getToken( method, url, extraParams, this )
    }
    return null
  }

  getRefreshToken( method, url, extraParams ) {
    const auth = this.options.auth
    if ( auth && typeof auth.getRefreshToken === 'function' ) {
      return auth.getRefreshToken( method, url, extraParams, this )
    }
    return null
  }

  canRefreshToken( method, url, extraParams ) {
    const auth = this.options.auth
    if ( typeof auth === 'string' ) {
      return false
    }
    return auth && typeof auth.refreshToken === 'function'
  }

  #refreshToken( method, url, extraParams ) {
    const auth = this.options.auth
    if ( auth && typeof auth.refreshToken === 'function' ) {
      return auth.refreshToken( method, url, extraParams )
    }
    return null
  }

  setToken( jwt, type = 'Bearer' ) {
    this.options.auth = authToken( jwt, type )
  }

  /**
   * Sets the auth token to use, either a string or an object with
   * a getToken( method, url, extraParams, this ) function
   * @param {String|object} auth
   */
  setAuth( auth ) {
    this.options.auth = auth
  }

  get( url, opts ) {
    return this.makeRequest( 'GET', url, undefined, opts )
  }

  post( url, body, opts ) {
    return this.makeRequest( 'POST', url, body, opts )
  }

  put( url, body, opts ) {
    return this.makeRequest( 'PUT', url, body, opts )
  }

  patch( url, body, opts ) {
    return this.makeRequest( 'PATCH', url, body, opts )
  }

  delete( url, body, opts ) {
    return this.makeRequest( 'DELETE', url, body, opts )
  }

  child( path ) {
    const api = this
    return {
      get( options, extra ) { return api.get( path + api.encodeQueryParams( options ), extra ) },
      list( options, extra ) { return api.get( path + api.encodeQueryParams( options ), extra ) }, // same as get, but for distinct for other usages
      post( body, options, extra ) { return api.post( path + api.encodeQueryParams( options ), body, extra ) },
      put( body, options, extra ) { return api.put( path + api.encodeQueryParams( options ), body, extra ) },
      patch( body, options, extra ) { return api.patch( path + api.encodeQueryParams( options ), getPatchOpsArray( body ), extra ) },
      patchRaw( body, options, extra ) { return api.patch( path + api.encodeQueryParams( options ), body, extra ) },
      delete( body, options, extra ) { return api.delete( path + api.encodeQueryParams( options ), body, extra ) },
      child( subpath ) { return api.child( combinePath( path, subpath ) ) },
      path( ) { return path },
      api( ) { return api }
    }
  }

  encodeQueryParams( options ) {
    return encodeQ( options )
  }

  async headers( method, url, extraParams, options ) {
    const keys = options instanceof Object ? Object.keys( options ) : []
    const { accept, contentType : ct, useRefresh, ...rest } = options || {}
    const hdr = {
      ...rest
    }

    const acceptType = keys.includes( 'accept' ) ? options.accept : 'application/json'
    const contentType = keys.includes( 'contentType' ) ? options.contentType : 'application/json'
    if ( acceptType != null ) {
      hdr.Accept = acceptType
    }
    if ( contentType != null ) {
      hdr['Content-Type'] = contentType
    }
    let tkn
    if ( useRefresh && this.canRefreshToken( method, url, extraParams ) ) {
      tkn = this.getRefreshToken( method, url, extraParams )
    } else {
      tkn = this.getToken( method, url, extraParams )
    }
    if ( tkn ) {
      hdr.Authorization = tkn
    } else if ( !useRefresh && this.canRefreshToken( method, url, extraParams ) ) {
      await this.#refreshToken( method, url, extraParams )
      const nTkn = this.getToken( method, url, extraParams )
      hdr.Authorization = nTkn
    }
    return hdr
  }

  async makeRequest( method, url, body, options, canRetry = true ) {

    const {
      url : nUrl = url,
      opts : nOpts = options
    } = this.mutateRequest?.( url, options ) || {}

    const fullUrl = `${this.host}/${nUrl}`

    const { headers, log, useRefresh, ...rest } = nOpts || {}

    if ( this.isBodylessMethod( method ) ) {
      body = undefined // eslint-disable-line no-param-reassign
    }

    const opts = {
      ...rest,
      method,
      headers : await this.headers( method, fullUrl, null, { ...headers, useRefresh } ) 
    }
    if ( body instanceof FormData ) {
      opts.headers = {
        ...opts.headers,
        ...( typeof body.getHeaders === 'function' ? body.getHeaders() : {} )
      }
      opts.body = body
    } else if ( body && opts.headers['Content-Type'] === 'application/json' ) {
      opts.body = JSON.stringify( body )
    } else if ( body != null ) {
      opts.body = body
    } else {
      delete opts.headers['Content-Type']
    }

    if ( this.options.log === true || log === true ) {
      console.log( method, fullUrl, opts ) // eslint-disable-line no-console
    }

    try {
      return await this.runRequest( fullUrl, opts )
    } catch ( err ) {
      // only refresh if we didn't use the refresh token, but we have one
      if ( 
        options?.useRefresh !== true &&
        ( err.message === 'jwt expired' || err.message === 'no token' ) &&
        this.canRefreshToken( method, fullUrl, headers ) &&
        canRetry
      ) {
        await this.#refreshToken()
        return this.makeRequest( method, url, body, options, false )
      }
      throw err
    }
  }

  isBodylessMethod( method ) {
    if ( method?.toLowerCase() === 'get' || method?.toLowerCase() === 'head' ) {
      return true
    }

    return false
  }

  async runRequest( url, opts ) {
    const req = opts == null ? this.fetch( url ) : this.fetch( url, opts )
    try {
      const response = await req

      if ( !response.ok ) {
        if ( response.headers.get( 'Content-Type' )?.includes( 'application/json' ) ) {
          try {
            const json = await response.json()
            if ( json ) {
              throw ApiErrorFactory( json, response )
            } else {
              throw ApiErrorFactory( { status : response.status, message : response.statusText, body : json }, response )
            }
          } catch ( err ) {
            throw ApiErrorFactory( err, response )
          }
        }
      }

      if (
        response.headers.get( 'Content-Type' )?.includes( 'application/json' ) &&
        !( response.status === 204 || response.headers.get( 'Content-Length' ) === 0 || response.headers.get( 'Content-Length' ) === '0' )
      ) {
        return response.json()
      }

      return response
    } catch ( err ) {
      if ( err?.code === 'ENOTFOUND' ) {
        throw ApiErrorFactory( { status : 503, message : 'Unable to connect to server', url } )
      }
      throw err
    }
  }
}

export default Comms
